import React, { useState, useCallback, useRef, useEffect } from 'react'
import type { EntityWidgetProps } from '@/other/constants/EntityManagerConfig';
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { CardTitle } from "@/modern/components/ui/card";
import { Button as ButtonShadcn } from "@/modern/components/ui/button-shadcn";
import { Button } from "@/modern/components/ui/button";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/modern/components/ui/tooltip";
import { Save, X, FileText, Expand, PlusIcon, AlertTriangle, ZoomIn, ZoomOut } from "lucide-react";
import { Textarea } from "@/modern/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter } from "@/modern/components/ui/dialog-shadcn";
import { SharedModalBody, SharedModalDescription, SharedModal, SharedModalHeader, SharedModalTitle } from "@/modern/components/shared-modal-component";
import { cn } from '@/modern/utils';

export const EntityDescription = ({ record, onClose, headerTitle }: EntityWidgetProps) => {
  const { project } = useSwivuBryntumContext();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isZoomed, setIsZoomedState] = useState(() =>
    project?.userPreferencesStore?.getSetting(PERSISTENCE_STATE_KEYS.descriptionZoomState) === 'true' || false
  );
  const description = record?.description || "";

  const toggleZoom = useCallback(() => {
    setIsZoomedState(prev => {
      const next = !prev;
      project?.userPreferencesStore?.updateSetting(PERSISTENCE_STATE_KEYS.descriptionZoomState, String(next));
      return next;
    });
  }, [project]);

  const hasDescription = description && description.trim().length > 0;

  const handleSave = async (newDescription: string) => {
    record.set('description', newDescription);
  };

  return (
    <>
      <DashboardCard
        onClose={onClose}
        cardContentClassName="h-full flex flex-col p-4"
        header={
          <div className="flex items-center justify-between w-full">
            <CardTitle className="text-base gap-3 flex items-center truncate min-w-0">
              <span className="truncate min-w-0 pr-2">{headerTitle ?? "Description"}</span>
            </CardTitle>
            <ButtonGroup>
              {hasDescription && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ButtonShadcn
                      variant="entityHeader"
                      size="icon"
                      className={cn(isZoomed && "text-blue-500")}
                      onClick={toggleZoom}
                    >
                      {isZoomed ? <ZoomOut className="size-4" /> : <ZoomIn className="size-4" />}
                    </ButtonShadcn>
                  </TooltipTrigger>
                  <TooltipContent>{isZoomed ? "Default size" : "Zoom in"}</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <ButtonShadcn
                    variant="entityHeader"
                    className={cn(description && "w-auto px-3")}
                    size="icon"
                    onClick={() => setIsDialogOpen(true)}
                  >
                    {!description ? <PlusIcon className="size-4" /> : <> <Expand className="size-4" /> Manage</>}
                  </ButtonShadcn>
                </TooltipTrigger>
                <TooltipContent>{!description ? "Add description" : "Edit description"}</TooltipContent>
              </Tooltip>
            </ButtonGroup>
          </div>
        }
      >
        <div className="h-full flex flex-col overflow-y-auto">
          {hasDescription ? (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <p className={cn(
                "text-foreground/90 leading-relaxed whitespace-pre-wrap pb-4 transition-[font-size] duration-300 ease-out",
                isZoomed ? "text-base" : "text-sm"
              )}>
                {description}
              </p>
            </div>
          ) : (
            <div className="min-h-[12rem] h-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 p-4 text-center shadow-sm">
              <div className="mx-auto flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <FileText className="size-5" />
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-foreground">Plain-Text Description</h3>
                <p className="text-xs text-muted-foreground">
                  Add a short, unformatted summary for the field.
                </p>
              </div>
              <Button
                onClick={() => setIsDialogOpen(true)}
                className="inline-flex items-center gap-2"
              >
                <PlusIcon className="size-4" />
                Add Description
              </Button>
            </div>
          )}
        </div>
      </DashboardCard>

      <DescriptionEditor
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        description={description}
        onSave={handleSave}
        isZoomed={isZoomed}
        onZoomToggle={toggleZoom}
      />
    </>
  )
}

const DescriptionEditor = ({
  open,
  onOpenChange,
  description,
  onSave,
  isZoomed,
  onZoomToggle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  description: string;
  onSave: (newDescription: string) => void;
  isZoomed: boolean;
  onZoomToggle: () => void;
}) => {
  const [localDescription, setLocalDescription] = useState(description);
  const [isSaving, setIsSaving] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasChanges = localDescription !== description;
  const wordCount = localDescription.trim().split(/\s+/).filter(Boolean).length;
  const charCount = localDescription.length;

  useEffect(() => {
    setLocalDescription(description);
  }, [description]);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.setSelectionRange(
          textareaRef.current.value.length,
          textareaRef.current.value.length
        );
      }, 100);
    }
  }, [open]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(localDescription);
      setIsOpen(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save description:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    // Check if there are unsaved changes
    if (hasChanges) {
      setShowUnsavedDialog(true);
    } else {
      setLocalDescription(description);
      setIsOpen(false);
      onOpenChange(false);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    } else {
      setIsOpen(open);
    }
  };

  // Handle dialog actions
  const handleKeepEditing = () => {
    setShowUnsavedDialog(false);
  };

  const handleDontSave = () => {
    setShowUnsavedDialog(false);
    setLocalDescription(description);
    setIsOpen(false);
    onOpenChange(false);
  };

  const handleSaveAndClose = async () => {
    await handleSave();
    setShowUnsavedDialog(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="z-[--swivu-bryntum-z-index] sm:max-w-5xl h-[80dvh] flex flex-col p-0 gap-0 focus-visible:outline-none rounded-md"
      >
        <div className="flex items-center justify-between p-4 border-b gap-2 min-w-0 flex-none">
          <div className="flex items-center gap-3 flex-1 overflow-hidden">
            <h2 className="text-xl text-muted-foreground">
              {description.trim().length === 0 ? "Add Description" : "Edit Description"}
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-none">
            <p className="text-sm text-muted-foreground mr-2 whitespace-nowrap">
              {wordCount} words, {charCount} characters
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className={cn(isZoomed && "text-blue-500")}
                  onClick={onZoomToggle}
                >
                  {isZoomed ? <ZoomOut className="size-4" /> : <ZoomIn className="size-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isZoomed ? "Default size" : "Zoom in"}</TooltipContent>
            </Tooltip>
            <Button
              type="submit"
              onClick={handleSave}
              size="sm"
              className="gap-2"
              disabled={!hasChanges || isSaving}
            >
              <Save />
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
            <Button type="button" variant="ghost" size="icon-sm" className="gap-2" onClick={handleClose}>
              <X />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex min-h-0 relative py-6 px-6">
          <Textarea
            ref={textareaRef}
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Add a short, unformatted summary for the field."
            className={cn("flex-1 min-h-0 resize-none leading-relaxed p-4 transition-[font-size] duration-300 ease-out", isZoomed ? "text-base md:text-base" : "text-sm")}
          />
        </div>
      </DialogContent>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <SharedModal className="md:max-w-md flex-col top-1/2 h-auto">
          <SharedModalHeader>
            <SharedModalTitle className="flex items-center gap-2 text-lg font-semibold">
              <AlertTriangle className="size-6 mr-2 text-yellow-500" /> Unsaved Changes
            </SharedModalTitle>
            <SharedModalDescription>You have unsaved changes. What would you like to do?</SharedModalDescription>
          </SharedModalHeader>
          <SharedModalBody>
            <DialogFooter className="flex-col sm:flex-row gap-3 border-t-0">
              <Button variant="ghost" onClick={handleKeepEditing} size="sm">
                Keep working
              </Button>
              <Button variant="destructive" onClick={handleDontSave} size="sm">
                Do not save
              </Button>
              <Button variant="success" onClick={handleSaveAndClose} size="sm">
                Save & close
              </Button>
            </DialogFooter>
          </SharedModalBody>
        </SharedModal>
      </Dialog>
    </Dialog>
  );
};
